// GRUNNBILDET — «ett rør» for åpne kilder.
//
// ── HVORFOR DENNE FILA FINNES ───────────────────────────────────────────────
// UX-rapportens kap. 08 (eierbestilling 2026-08-18, prioritet 2 etter
// kommunikasjonsdelen):
//
//   «Den vanligste feilen er å bolte inn hver åpne kilde for seg — én fane for
//   skip, én for trafikk, én for vær. Da får man tretti brytere og null følelse.
//   Skal OSINT være grunnstammen, må alt gjennom ett rør: kilde → adapter →
//   felles objektmodell → kart og feed. Da blir en politimelding, et skip fra AIS
//   og et trafikkamera samme slags objekt i grensesnittet, og "kart først" holder
//   helt ned i arkitekturen.»
//
// ⚠ ÆRLIG STATUS FØR DENNE FILA: `osint.ts` tok imot FRI TEKST fra en ekstern
// skraper og trakk ut MGRS/lat-lon → `Track` med `confidence: 0.2`. Det er én
// kilde-klasse (tekst) og én utgang (spor). Røret rapporten beskriver — flere
// klasser, to utganger, proveniens og levetid på hvert objekt — fantes ikke.
// `osint.ts` blir stående: den er tekst-adapteren i det nye røret, ikke noe
// denne fila erstatter.
//
// ── ⚠ FIRE TING SOM MÅ VÆRE RIKTIG, ELLERS LYVER GRUNNBILDET ────────────────
//
// 1. TO TIDER, IKKE ÉN. Et AIS-punkt er 40 sekunder gammelt når vi får det; en
//    politimelding kan være en time gammel. `tsMs` (da det skjedde) og
//    `mottattMs` (da vi fikk vite) er ULIKE tall, og å slå dem sammen er en løgn
//    om ferskhet. Samme lærepenge som `fraMs`/`tilMs` i `fotogrammetri.ts`.
//
// 2. LEVETID PÅ HVERT OBJEKT. Et trafikkuhell er over når veien er åpnet. Uten
//    levetid blir laget en samling gamle påstander som ser like ferske ut som de
//    nye — nøyaktig feilen `hendelseslogg.ts` retter med alder på hver linje.
//
// 3. LISENSEN HØRER PÅ OBJEKTET, IKKE I ET DOKUMENT. Rapportens egen advarsel:
//    «Vegkameraer og politilogger kan inneholde personopplysninger, og hver kilde
//    har egne bruksvilkår. Kildelisens bør ligge som metadata per lag.» Et
//    dokument ingen leser stopper ingen eksport; et felt på objektet gjør det.
//
// 4. «FLERE KILDER» MÅ BETY FLERE KILDER. Rapporten vil heve konfidensen ved
//    sammenfall. Men NRK og NTB som trykker samme NTB-melding er ÉN kilde, og en
//    konfidensheving på det er en målefeil som ser ut som bekreftelse. Derfor
//    bærer hver kilde et `opphav`, og `hevetKonfidens()` teller BARE uavhengige
//    opphav.
import { z } from 'zod';
import { kommuneMidtpunkt } from './kommunemidtpunkt.js';
// ── Kilderegisteret ─────────────────────────────────────────────────────────
/**
 * Rapportens tre klasser, som hver havner på sitt sted i grensesnittet:
 *
 *   • `stroem`    — AIS/skip, trafikkflyt, vegkamera, ADS-B, værradar
 *                   → KARTLAG man skrur av/på. Skal IKKE fylle tidslinja.
 *   • `hendelse`  — politilogg, NVE flom/skred, MET farevarsel, nyhetsblipp
 *                   → TIDSLINJA, som kort med hastegrad + markør.
 *   • `referanse` — vegnett, kritisk infrastruktur, dybde, grunnkart
 *                   → BUNNLAG som gir alt annet mening.
 *
 * ⚠ KLASSEN ER IKKE KOSMETIKK. Legger vi en sanntidsstrøm i tidslinja, drukner
 * operatørens meldinger i skipsposisjoner innen minuttet — og da har vi ødelagt
 * flata Fase 1 nettopp bygget.
 */
export const OsintKlasseSchema = z.enum(['stroem', 'hendelse', 'referanse']);
export const OSINT_KLASSE_LABEL = {
    stroem: 'Sanntidsstrøm',
    hendelse: 'Hendelsesvarsel',
    referanse: 'Referanse/kontekst',
};
/** Hvor objektene fra klassen havner — brukt i kildebiblioteket. */
export const OSINT_KLASSE_MAAL = {
    stroem: 'Kartlag (av/på) — fyller ikke tidslinja',
    hendelse: 'Tidslinja, som kort med hastegrad og markør',
    referanse: 'Bunnlag',
};
/**
 * Tillitsgrad til KILDEN (ikke til den enkelte observasjonen).
 *
 * ⚠ TO ULIKE STØRRELSER SOM STADIG BLANDES. En høytillitskilde kan levere en
 * usikker observasjon (NRK som skriver «meldinger tyder på»), og en midt-kilde
 * kan levere en presis (et vegkamera som viser kø). Kildens tillit er et tak på
 * hva vi kan tro, ikke en verdi vi kan bruke i stedet for konfidens.
 */
export const KildeTillitSchema = z.enum(['hoey', 'mid', 'lav']);
export const KILDE_TILLIT_LABEL = {
    hoey: 'Høy tillit',
    mid: 'Middels tillit',
    lav: 'Lav tillit',
};
/**
 * Lisensstatus.
 *
 * ⚠ `maa-avklares` ER EN GYLDIG OG VANLIG TILSTAND, og den skal si det høyt.
 * Alternativet — å gjette en lisensstreng for å få feltet fylt — ville gitt
 * systemet en juridisk påstand ingen har sjekket, og det er verre enn å ikke
 * vite. `lisensForbehold()` sørger for at et uavklart lag blir merket i UI-et
 * framfor å se like ryddig ut som et avklart.
 */
export const LisensStatusSchema = z.enum(['avklart', 'maa-avklares']);
export const KildeSchema = z.object({
    /** Stabil id (wire). */
    id: z.string().max(64),
    navn: z.string().max(120),
    klasse: OsintKlasseSchema,
    tillit: KildeTillitSchema,
    /**
     * Opphav — den UAVHENGIGE informasjonskilden bak.
     *
     * ⚠ DETTE FELTET ER GRUNNEN TIL AT KONFIDENSHEVING KAN VÆRE ÆRLIG. NRK og NTB
     * som trykker samme NTB-melding har SAMME opphav (`ntb`), og teller derfor som
     * én. To kilder med ulikt opphav som sier det samme er reell bekreftelse.
     * Settes lik `id` når kilden ER sitt eget opphav.
     */
    opphav: z.string().max(64),
    lisens: z.string().max(200).default(''),
    lisensStatus: LisensStatusSchema,
    /**
     * Hvor ofte kilden NORMALT oppdaterer (ms). Brukes til ferskhetsprikken.
     *
     * ⚠ MÅ VÆRE PER KILDE. AIS oppdaterer i sekunder, et farevarsel i timer. Én
     * felles terskel ville malt farevarselet grått («dødt lag») hver gang det ikke
     * var noe å varsle om — altså gjort normal drift til en feilmelding.
     */
    forventetIntervallMs: z.number().int().positive(),
    /** Personopplysninger kan forekomme (GDPR-vurdering kreves). */
    personopplysninger: z.boolean().default(false),
    url: z.string().max(300).default(''),
});
// ── Objektet ────────────────────────────────────────────────────────────────
export const ProveniensSchema = z.object({
    kildeId: z.string().max(64),
    kildeNavn: z.string().max(120),
    /** Lisensstrengen KOPIERT inn på objektet, se filhodet punkt 3. */
    lisens: z.string().max(200).default(''),
    lisensStatus: LisensStatusSchema,
    /** Da VI hentet den. Ulikt både `tsMs` og `mottattMs` ved batch-henting. */
    hentetMs: z.number(),
    /** Lenke tilbake til kilden. Opphavsrett: vi viser utdrag + lenke, ikke alt. */
    url: z.string().max(2048).optional(),
});
export const PRESISJON_LABEL = {
    punkt: 'Punkt (stedfestet)',
    naeromraade: 'Nærområde (± noen hundre meter)',
    kommune: 'ESTIMERT — bare kommunen er kjent',
    fylke: 'ESTIMERT — bare fylket er kjent',
    omraade: 'Gjelder hele området (kjent flate)',
    ukjent: 'UKJENT STED — ingen posisjon',
};
/**
 * Forbeholdet som MÅ stå ved posisjonen.
 *
 * ⚠ TOM STRENG BARE FOR `punkt`. Alle andre nivåer er påstander med forbehold, og
 * forbeholdet skal stå INNTIL posisjonen — samme regel som `produktForbehold()` i
 * `fotogrammetri.ts` og caveat-regelen i designsystemet.
 *
 * ⚠ TEKSTEN SIER «DRA MARKØREN». Et forbehold som bare beskriver problemet
 * etterlater operatøren med en advarsel og ingen handling; her ER handlingen
 * tilgjengelig, og da skal den nevnes.
 */
export function presisjonsForbehold(p) {
    switch (p) {
        case 'punkt': return '';
        // ⚠ INGEN FORBEHOLD, OG DET ER POENGET. En kjent flate er en PRESIS opplysning,
        // ikke et estimat. Å skrive «⚠ omtrentlig» på et skredvarsel ville lært
        // operatøren å mistro nettopp det hun skal handle på. Setningen er en
        // OPPLYSNING om utstrekning, ikke en advarsel om kvalitet — og den mangler
        // derfor ⚠-merket med vilje.
        case 'omraade': return 'Gjelder hele det markerte området, ikke ett punkt';
        case 'naeromraade': return '⚠ Omtrentlig sted — stedsnavnet ga flere kandidater innen noen hundre meter. Dra markøren om du vet bedre.';
        case 'kommune': return '⚠ ESTIMERT POSISJON: bare kommunen er kjent. Markøren står i kommunens geometriske midtpunkt — IKKE der hendelsen var. Dra den dit den hører.';
        case 'fylke': return '⚠ ESTIMERT POSISJON: bare fylket er kjent. Markøren er et midtpunkt, ikke et sted. Dra den dit den hører.';
        case 'ukjent': return '⚠ Ingen posisjon kunne fastsettes — hendelsen står uten sted. Dra den inn på kartet om du vet hvor den var.';
    }
}
export const POSISJON_KILDE_LABEL = {
    kilde: 'Fra kilden (ferdig koordinat)',
    stedsnavn: 'Geokodet fra stedsnavn (Kartverket)',
    kommunemidtpunkt: 'Kommunens midtpunkt — et estimat, ikke et sted',
    operator: 'Satt av operatør',
};
/**
 * Har posisjonen et menneske bak seg?
 *
 * ⚠ FINNES FOR AT KARTET SKAL KUNNE SLUTTE Å ADVARE. Har en operatør dratt
 * markøren dit hun mener hendelsen var, er «⚠ estimert posisjon» ikke lenger sant
 * — og et forbehold som står igjen etter at det er rettet, lærer operatøren å
 * ignorere forbehold.
 */
export function erMenneskesatt(k) {
    return k === 'operator';
}
export const GrunnbildeObjektSchema = z.object({
    id: z.string().max(200),
    klasse: OsintKlasseSchema,
    /** Kildens egen typebetegnelse: 'skip', 'trafikkuhell', 'snoeskredvarsel' … */
    type: z.string().max(64),
    /** Én linje: hva dette er. */
    tittel: z.string().max(300),
    /** Kort utdrag. ⚠ UTDRAG, ikke hele artikkelen — se `MAKS_UTDRAG`. */
    utdrag: z.string().max(600).default(''),
    lat: z.number().min(-90).max(90).optional(),
    lon: z.number().min(-180).max(180).optional(),
    /** DA DET SKJEDDE. Se filhodet punkt 1. */
    tsMs: z.number(),
    /** DA VI FIKK VITE. Alltid ≥ `tsMs` i praksis; ulikheten er informasjon. */
    mottattMs: z.number(),
    /**
     * Levetid fra `tsMs` — når objektet FORSVINNER fra det levende bildet.
     *
     * ⚠ PÅKREVD, IKKE VALGFRI. Et objekt uten levetid ville sett like ferskt ut om
     * fire timer. Vet adapteren ikke bedre, skal den sette et konservativt tall og
     * si det i `forbehold` — ikke la feltet stå tomt.
     *
     * ⚠ IKKE DET SAMME SOM `foreldetMs`. Se under.
     */
    levetidMs: z.number().int().positive(),
    /**
     * Når objektet blir FORELDET — altså grått, men fortsatt der.
     *
     * ── ⚠ EIERBESLUTNING 2026-08-18 ─────────────────────────────────────────────
     *   «I skade legges det i tidslinja, blir grått etter et døgn.» … «i GNÅ skal
     *   nyheten droppes etter en uke.»
     *
     * Det krever TO tall, ikke ett, og forskjellen er hele poenget:
     *   • `foreldetMs`  — når vi slutter å STOLE på at det er ferskt (1 døgn)
     *   • `levetidMs`   — når det slutter å VÆRE der (1 uke)
     *
     * Hadde vi bare hatt levetid, måtte vi valgt mellom to dårlige utfall: droppe
     * etter et døgn (og miste en pågående sak som varer i tre dager), eller la den
     * stå fram til uka er ute uten å si at den er gammel. Grå-tilstanden er
     * mellomrommet der sannheten bor.
     *
     * ⚠ GRÅ BETYR «IKKE OPPDATERT SIDEN …», IKKE «AVSLUTTET». En savnetsak eller en
     * stengt vei kan vare i dager; at politiet ikke har meldt noe nytt på 25 timer
     * sier ingenting om at saken er over. `foreldelsesTekst()` er ordlagt etter det,
     * og det er en viktigere presisjon enn den ser ut som: leser en operatør grått
     * som «håndtert», har gråfargen gjort skade i stedet for å hjelpe.
     *
     * Valgfri fordi et referanselag (grunnkart, vegnett) ikke foreldes på samme måte
     * — det er ikke en observasjon, det er et bakteppe.
     */
    foreldetMs: z.number().int().positive().optional(),
    /** 0–1. Kildens tillit er et TAK på denne, ikke en erstatning. */
    konfidens: z.number().min(0).max(1),
    proveniens: ProveniensSchema,
    /** Fritt forbehold fra adapteren — leses av operatøren, ikke av kode. */
    forbehold: z.string().max(400).default(''),
    /**
     * Hvor godt vi vet hvor det er. Se `Presisjon` for eierbeslutningen bak.
     *
     * Valgfri fordi et referanselag ikke har ett sted i det hele tatt.
     */
    presisjon: z.enum(['punkt', 'naeromraade', 'kommune', 'fylke', 'omraade', 'ukjent']).optional(),
    /**
     * Usikkerhetsradius i meter — «vi vet det er innenfor denne sirkelen».
     *
     * ⚠ ET EKTE TALL, IKKE EN PYNTET SIRKEL. For kommune-nivå er det avstanden til
     * kommunens fjerneste hjørne (`kommunemidtpunkt.ts`), som for en fjellkommune
     * blir titalls kilometer. Det ser stort ut, og det SKAL det: en liten sirkel på
     * en stor usikkerhet er en løgn som ser presis ut.
     */
    usikkerhetM: z.number().nonnegative().optional(),
    /** Hvem posisjonen kom fra. Proveniens, ikke kvalitet — se `PosisjonKilde`. */
    posisjonKilde: z.enum(['kilde', 'stedsnavn', 'kommunemidtpunkt', 'operator']).optional(),
    /**
     * Polygonringer når kilden oppgir en FLATE (MET-farevarsel, NVE-skredregion).
     *
     * ⚠ FLATA ER DEN SANNE REPRESENTASJONEN, `lat`/`lon` er bare et anker for
     * etiketten. Tegner kartlaget punktet uten polygonet, sier det at faren er der og
     * ikke i resten av området — og det er en farligere feil enn å ikke vise varselet
     * i det hele tatt.
     *
     * Ytre ring først. Tom/utelatt = ingen flate.
     */
    omraade: z.array(z.array(z.object({
        lat: z.number().min(-90).max(90),
        lon: z.number().min(-180).max(180),
    })).max(4000)).max(50).optional(),
    /**
     * Operatørens retting av posisjonen (eierbeslutning 2026-08-18).
     *
     * ⚠ BEVARER DEN OPPRINNELIGE POSISJONEN. Uten `fraLat`/`fraLon` kan ingen se hva
     * systemet mente og hva mennesket mente — og da er rettingen ikke etterprøvbar.
     * Er estimatet og rettingen langt fra hverandre, er det i seg selv en opplysning
     * om at geokodingen bommet på nettopp den typen sted.
     */
    flyttetAv: z.object({
        av: z.string().max(128),
        tsMs: z.number(),
        fraLat: z.number().optional(),
        fraLon: z.number().optional(),
        /** Operatørens egen begrunnelse, om hun ga en. */
        grunn: z.string().max(300).default(''),
    }).optional(),
});
/**
 * Maks tegn i et utdrag.
 *
 * ⚠ OPPHAVSRETT, IKKE PLASSHENSYN. Rapportens advarsel: «Vis overskrift + kort
 * utdrag + lenke til kilden, aldri hele artikkelen.» Skjemaet håndhever taket, så
 * en adapter ikke kan lekke en full artikkel inn i grunnbildet ved uhell.
 */
export const MAKS_UTDRAG = 600;
export function parseGrunnbildeObjekt(raa) {
    const r = GrunnbildeObjektSchema.safeParse(raa);
    return r.success ? r.data : null;
}
// ── Levetid ─────────────────────────────────────────────────────────────────
// ⚠ NAVNENE ER PREFIKSET MED VILJE. `feed.ts` har sine egne `erGyldig`/
// `levetidTekst` for feed-innlegg, og de to levetidsbegrepene er ULIKE: et
// feed-innlegg utløper fordi avsenderen satte en frist, et grunnbildeobjekt fordi
// virkeligheten har flyttet seg. Delte de navn, ville en importfeil gitt riktig
// type og galt resonnement — den vanskeligste feilen å finne.
export function grunnbildeGyldig(o, naaMs) {
    return naaMs - o.tsMs < o.levetidMs;
}
/**
 * Er objektet FORELDET — grått, men fortsatt i bildet?
 *
 * ⚠ `false` NÅR `foreldetMs` MANGLER, ikke `true`. Et objekt som ikke har en
 * foreldelsesfrist skal ikke gråes ut på gjetning; et referanselag er ikke gammelt
 * fordi det er fra i fjor.
 */
export function erForeldet(o, naaMs) {
    if (o.foreldetMs === undefined)
        return false;
    return naaMs - o.tsMs >= o.foreldetMs;
}
/**
 * Teksten som følger en foreldet rad.
 *
 * ⚠ ORDLYDEN ER BESLUTNINGEN. «Ikke oppdatert siden …» sier at KILDEN har vært
 * stille; «avsluttet» eller «utgått» ville sagt at SAKEN er over, og det vet vi
 * ikke. En savnetsak eller en stengt vei kan vare i dager. Leser en operatør grått
 * som «håndtert», har gråfargen gjort skade i stedet for å hjelpe.
 *
 * Tom streng når objektet ikke er foreldet — flata skal ikke måtte sjekke to
 * ganger.
 */
export function foreldelsesTekst(o, naaMs) {
    if (!erForeldet(o, naaMs))
        return '';
    const t = Math.floor((naaMs - o.tsMs) / 3_600_000);
    const alder = t < 48 ? `${t} t` : `${Math.floor(t / 24)} d`;
    return `Ikke oppdatert av kilden på ${alder} — saken kan fortsatt være åpen`;
}
/**
 * Hvor lenge objektet har igjen, som tekst.
 *
 * ⚠ SIER «UTGÅTT» MED ALDER, ikke bare «utgått». Et varsel som gikk ut for to
 * minutter siden og ett som gikk ut i går er to helt ulike situasjoner, og bare
 * det siste er trygt å ignorere.
 */
export function grunnbildeLevetidTekst(o, naaMs) {
    const igjen = o.levetidMs - (naaMs - o.tsMs);
    if (igjen <= 0) {
        const siden = Math.floor(-igjen / 60_000);
        return siden < 60 ? `Utgått for ${Math.max(0, siden)} min siden` : `Utgått for ${Math.floor(siden / 60)} t siden`;
    }
    const min = Math.floor(igjen / 60_000);
    if (min < 60)
        return `Gyldig ${min} min til`;
    return `Gyldig ${Math.floor(min / 60)} t til`;
}
/**
 * Forsinkelsen fra hendelse til vi visste, som tekst — eller tom når den er
 * ubetydelig.
 *
 * ⚠ NAVNGIR FORSINKELSEN NÅR DEN BETYR NOE. Et skip som var her for fem minutter
 * siden er ikke her nå, og en operatør som tror posisjonen er sanntid legger et
 * kryss på feil sted i kartet.
 */
export function grunnbildeForsinkelseTekst(o) {
    const d = o.mottattMs - o.tsMs;
    if (d < 60_000)
        return '';
    const min = Math.floor(d / 60_000);
    return min < 60
        ? `⚠ ${min} min gammel da vi fikk den`
        : `⚠ ${Math.floor(min / 60)} t gammel da vi fikk den`;
}
export const FERSKHET_LABEL = {
    fersk: 'Fersk',
    forsinket: 'Forsinket',
    doed: 'DØDT LAG — ingen data',
    ukjent: 'Ikke forsøkt hentet',
};
/** Forsinket ved 3× forventet intervall, dødt ved 10×. */
export const FERSKHET_FORSINKET = 3;
export const FERSKHET_DOED = 10;
/**
 * Ferskhet for én kilde.
 *
 * `sisteMottattMs === null` betyr at vi ikke har hentet ennå — og det skal si
 * `ukjent`, ALDRI `doed`. «Vi har ikke spurt» og «kilden svarer ikke» er to ulike
 * påstander, og bare den andre er en feil.
 */
export function ferskhet(k, sisteMottattMs, naaMs) {
    if (sisteMottattMs === null)
        return 'ukjent';
    const alder = naaMs - sisteMottattMs;
    if (alder > k.forventetIntervallMs * FERSKHET_DOED)
        return 'doed';
    if (alder > k.forventetIntervallMs * FERSKHET_FORSINKET)
        return 'forsinket';
    return 'fersk';
}
/**
 * Teksten ved prikken.
 *
 * ⚠ ALDEREN STÅR I TEKST, IKKE BARE I FARGEN. En fargeprikk alene er ubrukelig
 * for en fargeblind operatør og uleselig på en skjerm med nattfilter — samme
 * regel som hastegrads-stripa i bølge 215.
 */
export function ferskhetTekst(k, sisteMottattMs, naaMs) {
    const f = ferskhet(k, sisteMottattMs, naaMs);
    if (f === 'ukjent')
        return FERSKHET_LABEL.ukjent;
    const s = Math.max(0, Math.floor((naaMs - (sisteMottattMs ?? naaMs)) / 1000));
    const alder = s < 60 ? `${s} s` : s < 3600 ? `${Math.floor(s / 60)} min` : `${Math.floor(s / 3600)} t`;
    return `${FERSKHET_LABEL[f]} · sist ${alder} siden`;
}
// ── Konfidens ved sammenfall ────────────────────────────────────────────────
/**
 * Hev konfidensen når UAVHENGIGE kilder sier det samme.
 *
 * ⚠ TELLER OPPHAV, IKKE KILDER. Rapporten vil heve konfidensen ved sammenfall,
 * og det er riktig — men NRK og NTB som trykker samme NTB-melding har samme
 * opphav og er ÉN bekreftelse. Talte vi kilder, ville systemet hevet sin egen
 * tillit til seg selv hver gang en wire ble videreformidlet, og et rykte med fem
 * avsendere ville sett ut som fem vitner.
 *
 * Formelen er bevisst konservativ: hvert EKSTRA uavhengige opphav lukker en
 * tredjedel av gapet opp mot 1, og taket er 0,95. Full sikkerhet fra åpne kilder
 * finnes ikke, og et tall som viser 1,0 ville invitert til å behandle et
 * nyhetsoppslag som en bekreftet observasjon.
 */
export const KONFIDENS_TAK = 0.95;
export function hevetKonfidens(objekter, opphav) {
    if (!objekter.length) {
        return { konfidens: 0, uavhengige: 0, forklaring: 'Ingen kilder' };
    }
    const unike = [...new Set(opphav)];
    const beste = Math.max(...objekter.map((o) => o.konfidens));
    let k = beste;
    for (let i = 1; i < unike.length; i++)
        k = k + (1 - k) / 3;
    const konfidens = Math.min(KONFIDENS_TAK, k);
    // ⚠ FORKLARINGEN NAVNGIR HVA SOM BLE TALT. Et tall som har steget uten at noen
    // kan se hvorfor er et tall ingen kan overprøve.
    const forklaring = unike.length > 1
        ? `${unike.length} uavhengige opphav (${objekter.length} oppslag): ${unike.join(', ')}`
        : `Én kilde (${objekter.length} oppslag fra samme opphav: ${unike[0] ?? 'ukjent'}) — ingen bekreftelse`;
    return { konfidens, uavhengige: unike.length, forklaring };
}
// ── Jus: forbeholdet som må følge laget ─────────────────────────────────────
/**
 * Forbeholdene som MÅ vises for en kilde. Tom liste = ingenting å si.
 *
 * ⚠ RETURNERER TEKSTER, IKKE ET BOOLEAN. Et `kanBrukes()`-flagg ville tvunget
 * fram et ja/nei ingen her har myndighet til å gi — og satt til `false` som
 * standard ville det blokkert hele grunnbildet til en jurist hadde gått gjennom
 * hver kilde. Riktig oppførsel er å VISE forbeholdet og la mennesket bestemme,
 * på samme måte som `produktForbehold()` i `fotogrammetri.ts`.
 */
export function lisensForbehold(k) {
    const ut = [];
    if (k.lisensStatus === 'maa-avklares') {
        ut.push(`⚠ Lisens IKKE avklart for «${k.navn}» — gjenbruk og videreformidling må avklares mot kildens vilkår før eksport`);
    }
    if (k.personopplysninger) {
        ut.push(`⚠ «${k.navn}» kan inneholde personopplysninger — GDPR-vurdering kreves før lagring og deling`);
    }
    return ut;
}
/**
 * ⚠ GRADERING VED FUSJON — rapportens andre advarsel, og den farligste.
 *
 *   «OSINT er ugradert, men fusjonen med eget bilde kan bli gradert. Systemet må
 *   hindre at ugraderte lag drar med seg gradert kontekst feil vei.»
 *
 * Den konkrete risikoen er ikke at et OSINT-objekt er gradert — det er det ikke.
 * Risikoen er at et UTSNITT som inneholder både OSINT og egne enheter avslører
 * EGEN DISPOSISJON, selv om hvert lag isolert er ugradert. Et skjermbilde av
 * «AIS + nyhetsblipp + egne tropper» er en gradert opplysning satt sammen av tre
 * ugraderte.
 *
 * Denne funksjonen er derfor et VARSEL på eksportveien, ikke en klassifisering:
 * den sier at utsnittet ikke lenger kan behandles som ugradert fordi det er
 * blandet. Selve grensekontrollen hører i `classification.ts`/`datadiode.ts`.
 */
export function fusjonsForbehold(harOsint, harEgneEnheter) {
    if (!harOsint || !harEgneEnheter)
        return null;
    return '⚠ BLANDET UTSNITT: åpne kilder er tegnet sammen med egne enheter. '
        + 'Hvert lag er ugradert for seg, men sammenstillingen kan avsløre egen disposisjon '
        + 'og skal ikke behandles som ugradert. Avklar før eksport eller deling.';
}
// ── Estimert posisjon og operatørens retting (eierbeslutning 2026-08-18) ─────
/**
 * Sett en ESTIMERT posisjon fra kommunens midtpunkt.
 *
 * Dette er veien eier valgte framfor «ingen posisjon»: hendelsen kommer PÅ kartet,
 * med et ekte usikkerhetstall og et forbehold som ber operatøren rette den.
 *
 * ⚠ `null` FOR UKJENT KOMMUNE, aldri et oppdiktet punkt. En hendelse uten kjent
 * kommune skal fortsatt stå uten sted — «et sted i Norge» er ikke et estimat, det
 * er ingen informasjon tegnet som om den var informasjon.
 */
export function estimerFraKommune(o, knr) {
    const m = kommuneMidtpunkt(knr);
    if (!m)
        return null;
    return {
        ...o,
        lat: m.lat,
        lon: m.lon,
        presisjon: 'kommune',
        usikkerhetM: m.radiusM,
        posisjonKilde: 'kommunemidtpunkt',
        // ⚠ FORBEHOLDET SKRIVES PÅ NYTT, ikke lagt til. Sto det gamle igjen («ingen
        // posisjon kunne fastsettes») sammen med det nye, ville de motsagt hverandre.
        forbehold: settForbehold(o.forbehold, presisjonsForbehold('kommune')),
    };
}
/**
 * Bytt ut presisjonsforbeholdet, behold resten.
 *
 * Forbeholdsfeltet bærer flere setninger (presisjon + f.eks. alvorlighetens
 * proveniens), skilt med « · ». ⚠ Vi kan ikke bare erstatte hele strengen: da mistet
 * vi begrunnelsen for alvorligheten. Vi kan heller ikke bare legge til: da står to
 * motstridende presisjonspåstander. Derfor fjernes de setningene som ER et
 * presisjonsforbehold, og bare de.
 */
function settForbehold(gammelt, nytt) {
    const alle = new Set(['punkt', 'naeromraade', 'kommune', 'fylke', 'ukjent']
        .map((p) => presisjonsForbehold(p))
        .filter(Boolean));
    const beholdt = gammelt.split(' · ').map((d) => d.trim()).filter((d) => d && !alle.has(d));
    return [nytt, ...beholdt].filter(Boolean).join(' · ').slice(0, 400);
}
/**
 * Operatøren har flyttet markøren.
 *
 * ── ⚠ HVA SOM SKJER MED FORBEHOLDET ─────────────────────────────────────────
 * Presisjonen blir `punkt` og forbeholdet FJERNES. Det er ikke en forenkling: har
 * en operatør dratt markøren dit hun mener hendelsen var, er «⚠ estimert posisjon,
 * dra den dit den hører» ikke lenger sant — og et forbehold som står igjen etter at
 * det er rettet, lærer operatøren å ignorere forbehold. Da virker heller ikke de
 * som betyr noe.
 *
 * ⚠ PROVENIENSEN ERSTATTER FORBEHOLDET, den forsvinner ikke. `flyttetAv` bærer hvem,
 * når, hvorfra og hvorfor, og kartet/detaljpanelet viser «Satt av @ivar 14:22 —
 * flyttet 2,4 km fra estimatet». Det er en sterkere opplysning enn et forbehold:
 * den navngir et menneske som kan spørres.
 */
export function flyttObjekt(o, lat, lon, av, naaMs, grunn = '') {
    return {
        ...o,
        lat,
        lon,
        presisjon: 'punkt',
        // ⚠ USIKKERHETEN FJERNES HELT. En sirkel rundt et punkt et menneske har pekt
        // ut er meningsløs: hun pekte, hun anslo ikke.
        usikkerhetM: undefined,
        posisjonKilde: 'operator',
        forbehold: settForbehold(o.forbehold, ''),
        flyttetAv: {
            av,
            tsMs: naaMs,
            // Bevarer hva SYSTEMET mente, så rettingen er etterprøvbar.
            ...(o.lat !== undefined ? { fraLat: o.lat } : {}),
            ...(o.lon !== undefined ? { fraLon: o.lon } : {}),
            grunn,
        },
    };
}
/**
 * Én linje om posisjonen — til detaljpanel og markørtittel.
 *
 * ⚠ SIER ALLTID NOE. En posisjon uten opplysning om hvor den kom fra ser like
 * sikker ut enten den er målt, geokodet eller gjettet — og det er hele problemet
 * eierbeslutningen skal løse.
 */
export function posisjonsTekst(o, naaMs) {
    if (o.lat === undefined || o.lon === undefined) {
        return 'Ingen posisjon — hendelsen står bare i tidslinja';
    }
    if (o.flyttetAv) {
        const min = Math.max(0, Math.floor((naaMs - o.flyttetAv.tsMs) / 60_000));
        const alder = min < 1 ? 'nå' : min < 60 ? `${min} min siden` : `${Math.floor(min / 60)} t siden`;
        const flyttet = flyttetAvstandM(o);
        const deler = [`Satt av ${o.flyttetAv.av} (${alder})`];
        // ⚠ AVSTANDEN FRA ESTIMATET ER EN OPPLYSNING I SEG SELV: ble markøren flyttet
        // 40 km, bommet geokodingen grovt på nettopp den typen sted, og det er verdt å
        // se. Ble den flyttet 30 meter, var estimatet i praksis riktig.
        if (flyttet !== null)
            deler.push(`flyttet ${formatAvstand(flyttet)} fra estimatet`);
        if (o.flyttetAv.grunn)
            deler.push(o.flyttetAv.grunn);
        return deler.join(' · ');
    }
    const kilde = o.posisjonKilde ? POSISJON_KILDE_LABEL[o.posisjonKilde] : 'Ukjent posisjonskilde';
    if (o.usikkerhetM !== undefined && o.usikkerhetM > 0) {
        return `${kilde} · usikkerhet ± ${formatAvstand(o.usikkerhetM)}`;
    }
    return kilde;
}
/** Hvor langt operatøren flyttet markøren, eller `null` når vi ikke vet hvorfra. */
export function flyttetAvstandM(o) {
    const f = o.flyttetAv;
    if (!f || f.fraLat === undefined || f.fraLon === undefined)
        return null;
    if (o.lat === undefined || o.lon === undefined)
        return null;
    const R = 6_371_000;
    const rad = Math.PI / 180;
    const dLat = (o.lat - f.fraLat) * rad;
    const dLon = (o.lon - f.fraLon) * rad;
    const a = Math.sin(dLat / 2) ** 2
        + Math.cos(f.fraLat * rad) * Math.cos(o.lat * rad) * Math.sin(dLon / 2) ** 2;
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}
/** Avstand som lesbar tekst. Meter under en kilometer, ellers km med én desimal. */
export function formatAvstand(m) {
    return m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1)} km`;
}
/**
 * Skal kartet tegne en usikkerhetssirkel?
 *
 * ⚠ IKKE FOR MENNESKESATTE PUNKTER, og ikke for små usikkerheter. En sirkel på 40
 * meter er mindre enn markøren og gir bare visuell støy; en sirkel rundt et punkt
 * en operatør har pekt ut er direkte feil.
 */
export const MIN_SIRKEL_M = 150;
export function skalTegneUsikkerhet(o) {
    if (erMenneskesatt(o.posisjonKilde))
        return false;
    // ⚠ EN KJENT FLATE HAR INGEN USIKKERHETSSIRKEL. Den har en GEOMETRI, og den skal
    // tegnes. En sirkel rundt ankeret ville konkurrert med polygonet og antydet at
    // faren egentlig er der i midten.
    if (o.presisjon === 'omraade')
        return false;
    return (o.usikkerhetM ?? 0) >= MIN_SIRKEL_M;
}
//# sourceMappingURL=grunnbilde.js.map